/**
 * PATH: packages/email-hub/src/feature/components/rich-text/QDriveImagePicker.tsx
 * FILE_ID: EMAILHUB-RICHTEXT-QDRIVEIMG-001
 * PURPOSE: Search-and-pick modal for inserting an image from QDrive (org file
 *          store) into the email body. Hits GET /api/qdrive/search, filters to
 *          image/* results, then fetches the picked file's presigned bytes and
 *          hands back a SELF-CONTAINED data: URL so the sent email renders the
 *          image in any client (presigned S3 links expire ~1h — unsafe in mail).
 * FLOW: query → /api/qdrive/search → pick → /files/<id>/download → fetch bytes
 *       → compressImageToDataUrl → onPick(dataUrl, alt)
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Image as ImageIcon, Loader2, Search, X } from 'lucide-react'
import { emailApiFetch } from '../../lib/api'
import { compressImageToDataUrl } from '../../lib/imageClipboard'

interface QDriveFileRow {
  id: string
  name?: string
  filename?: string
  title?: string
  content_type?: string
}

function rowName(r: QDriveFileRow): string {
  return r.name || r.filename || r.title || 'image'
}

export function QDriveImagePicker({ open, onClose, onPick }: {
  open: boolean
  onClose: () => void
  onPick: (dataUrl: string, alt: string) => void
}) {
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<QDriveFileRow[]>([])
  const [loading, setLoading] = useState(false)
  const [fetchingId, setFetchingId] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setQuery(''); setRows([]); setErr(null); setFetchingId(null)
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < 2) { setRows([]); setLoading(false); return }
    setLoading(true)
    const ctrl = new AbortController()
    const timer = window.setTimeout(async () => {
      try {
        const res = await emailApiFetch(
          `/api/qdrive/search?q=${encodeURIComponent(q)}`,
          { signal: ctrl.signal } as RequestInit,
        )
        const data = await res.json().catch(() => ({ results: [] }))
        if (ctrl.signal.aborted) return
        const all = (data.results as QDriveFileRow[]) || []
        setRows(all.filter((r) => (r.content_type || '').startsWith('image/')))
        setErr(null)
      } catch (e) {
        if (!ctrl.signal.aborted) setErr(e instanceof Error ? e.message : 'Search failed')
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    }, 220)
    return () => { ctrl.abort(); window.clearTimeout(timer) }
  }, [query, open])

  const pick = async (row: QDriveFileRow) => {
    setFetchingId(row.id)
    setErr(null)
    try {
      const meta = await emailApiFetch(`/api/qdrive/files/${row.id}/download`, {} as RequestInit)
      const metaJson = await meta.json().catch(() => ({}))
      const url: string | undefined = metaJson?.url || metaJson?.download_url
      if (!url) throw new Error('No download URL for this file')
      const blob = await (await fetch(url)).blob()
      const file = new File([blob], rowName(row), { type: blob.type || row.content_type || 'image/png' })
      const dataUrl = await compressImageToDataUrl(file)
      onPick(dataUrl, rowName(row))
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load that image')
    } finally {
      setFetchingId(null)
    }
  }

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-start justify-center bg-black/40 pt-[12vh]"
      onMouseDown={onClose} role="dialog" aria-modal="true" aria-label="Insert image from QDrive">
      <div className="w-full max-w-lg rounded-xl border border-border bg-popover shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search QDrive images by name…" aria-label="Search QDrive images"
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none" />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <button type="button" onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-accent" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-80 overflow-y-auto py-1">
          {err && <div className="px-3 py-3 text-sm text-destructive">{err}</div>}
          {!err && query.trim().length < 2 && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">Type at least 2 characters to search your org files.</div>
          )}
          {!err && !loading && query.trim().length >= 2 && rows.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">No images found.</div>
          )}
          {rows.map((row) => (
            <button key={row.id} type="button" disabled={fetchingId !== null}
              onClick={() => void pick(row)}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-accent disabled:opacity-50">
              {fetchingId === row.id
                ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-indigo-500" />
                : <ImageIcon className="h-4 w-4 shrink-0 text-indigo-500" />}
              <div className="min-w-0">
                <div className="truncate text-sm text-foreground">{rowName(row)}</div>
                <div className="truncate text-xs text-muted-foreground">{row.content_type || 'image'}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}
